import React from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Radio,
  RadioIndicator,
  RadioIcon,
  Pressable,
  CircleIcon,
} from '@gluestack-ui/themed';
import { MathRenderer } from '@/common/components/MathRenderer';
import { MCQOption } from './types';
import { AspectImage } from './AspectImage';

interface OptionButtonProps {
  option: MCQOption;
  questionId: string;
  isSelected: boolean;
  onSelect: () => void;
  showCorrect?: boolean;
  isCorrect?: boolean;
  isDisabled?: boolean;
}

export const OptionButton: React.FC<OptionButtonProps> = ({
  option,
  questionId,
  isSelected,
  onSelect,
  showCorrect = false,
  isCorrect = false,
  isDisabled = false,
}) => {
  const getBorderColor = () => {
    if (showCorrect) {
      if (isCorrect) return '$success500';
      if (isSelected && !isCorrect) return '$error500';
    }
    return isSelected ? '$primary500' : '$borderLight';
  };

  const getBackgroundColor = () => {
    if (showCorrect) {
      if (isCorrect) return '$success50';
      if (isSelected && !isCorrect) return '$error50';
    }
    return isSelected ? '$primary50' : '$bgCard';
  };

  return (
    <Pressable
      testID={`mcq-option-button-${questionId}-${option.id}`}
      accessibilityState={{ selected: isSelected }}
      onPress={onSelect}
      disabled={isDisabled}
    >
      <Box
        borderWidth={2}
        borderColor={getBorderColor()}
        bg={getBackgroundColor()}
        borderRadius="$md"
        p="$3"
        mb="$2"
      >
        <HStack space="md" alignItems="center">
          <Radio value={option.id} isDisabled={isDisabled}>
            <RadioIndicator alignItems="center" justifyContent="center">
              <RadioIcon as={CircleIcon} />
            </RadioIndicator>
          </Radio>

          <VStack flex={1} space="sm">
            <MathRenderer>{option.text}</MathRenderer>

            {!!option.imageUrl && (
              <Box mt="$1">
                <AspectImage
                  uri={option.imageUrl}
                  alt={option.imageAlt || 'Option image'}
                  maxHeight={220}
                />
              </Box>
            )}

            {showCorrect && isCorrect && (
              <Text size="sm" color="$success600" bold>
                Correct answer
              </Text>
            )}
            {showCorrect && isSelected && !isCorrect && (
              <Text size="sm" color="$error600" bold>
                Incorrect
              </Text>
            )}
          </VStack>
        </HStack>
      </Box>
    </Pressable>
  );
};

export default OptionButton;
